import { faSliders } from '@fortawesome/free-solid-svg-icons';
import { generatePath, useNavigate } from 'react-router';

import { OverflowItem, OverflowMenu, OverflowMenuProps } from 'Components/Buttons/OverflowMenu';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table, TableProps } from 'Components/Table/Table';
import { DueDiligenceWorkflowProgressTableCellContent } from 'Components/UcrmTprm/DueDiligenceWorkflowProgressTableCellContent/DueDiligenceWorkflowProgressTableCellContent';
import { ICON_CALENDAR, ICON_CLOSE, ICON_DELETE_REMOVE, ICON_EDIT_MODIFY_UPDATE } from 'Config/Icons';
import { TPRM_SERVICE_DASHBOARD, TPRM_UPDATE_THIRD_PARTY, TPRM_UPDATE_THIRD_PARTY_SERVICE } from 'Config/PathTemplates';
import { buildUrlForCompletingIntake } from 'Helpers/UcrmTprm';
import { TPRM_ROLES } from 'Helpers/UserUtils';
import { OrganizationsAndAssessmentSubjectsTableRowData } from 'Models/AssessmentSubject';
import { AssessmentWorkflowState } from 'Models/DueDiligenceWorkflow';
import { ActiveState, FinalizationState, userCanSubmitOrFinalizeDraft, userHasFullAccessOrCreatedDraft } from 'Models/Organization';
import { ProductModule } from 'Models/ProductModule';
import { ServiceForNonTprmUser, ThirdPartyContact, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser, TprmEntities } from 'Models/TPRM';

import { ThirdPartiesAndServicesTableExpandedInformation } from './ThirdPartiesAndServicesTableExpandedInformation';

export interface ThirdPartiesAndServicesTableProps {
    onDisplayContacts: (contacts: ThirdPartyContact[], header: string) => void;
    onDisplayUsers: (userIds: string[], header: string) => void;
    onDeleteThirdParty: (thirdParty: ThirdPartyResponseWithServices | ThirdPartyWithServicesForNonTprmUser) => void;
    onDeleteService: (service: ThirdPartyService | ServiceForNonTprmUser) => void;
    onSetActiveStates: (thirdParty: ThirdPartyResponseWithServices) => void;
    onSetAssessmentDueDate: (service: ThirdPartyService) => void;
    onSetIrqTargetCompletionDate: (service: ThirdPartyService) => void;
    userTypeAndTableRowsData: { userHasFullTprmAccess: boolean; userId: string; tableRowsData: OrganizationsAndAssessmentSubjectsTableRowData<TprmEntities>[] };
}

export const ThirdPartiesAndServicesTable = (props: ThirdPartiesAndServicesTableProps) => {
    const navigate = useNavigate();

    /**
     * A helper function for comparing the service progress of two rows. Defined here for greater readability below.
     */
    const getNumericalValueForServiceProgress = (rowData: OrganizationsAndAssessmentSubjectsTableRowData<TprmEntities>): number => {
        if (!rowData.assessmentSubjectData) {
            // If a row does not have a service, sort secondarily by the third party's progress.
            switch (rowData.organization.finalization_state) {
                case FinalizationState.DRAFT_IN_PROGRESS:
                    return -3;
                case FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED:
                    return -2;
                case FinalizationState.FINALIZED:
                    return -1;
            }
        } else if (rowData.assessmentSubjectData.assessmentSubject.last_assessed && rowData.assessmentSubjectData.assessmentSubject.assessment_workflow_data.state === AssessmentWorkflowState.AWAITING_ASSESSMENT) {
            // If the row is for a service with a completed assessment, the row should drop towards the bottom of the table.
            return rowData.assessmentSubjectData.assessmentSubject.assessment_workflow_setup.due_date ? 100 : 99;
        } else {
            // For services in the middle of assessments, we simply sort by the progress on the assessment as represented by the number of checkmarks in the progress cell's hover.
            return rowData.assessmentSubjectData.dueDiligenceWorkflowProgress.completedStepNumber;
        }
    };

    const getOverflowMenuForRow = (rowData: OrganizationsAndAssessmentSubjectsTableRowData<TprmEntities>) => {
        // The point of returning a discriminated union here is to avoid the need for `as` in the conditionals below that construct the overflow menu options.
        // `thirdParty` and `service` need to be "pulled back out", via `const { thirdParty, service } = calculatedInfo;`, in various spots below in order to capture the `service` object, which could technically become `undefined` between renders, to be used in callbacks.
        const calculatedInfo:
            | {
                  userHasTprmRole: true;
                  thirdParty: ThirdPartyResponseWithServices;
                  service?: ThirdPartyService;
                  thirdPartyIsADraft: boolean;
                  serviceIsADraft: boolean;
                  userCanEditThirdParty: true;
                  userCanEditService: true;
                  userCanSubmitOrFinalizeThirdParty: boolean;
                  userCanSubmitOrFinalizeService: boolean;
              }
            | {
                  userHasTprmRole: false;
                  thirdParty: ThirdPartyWithServicesForNonTprmUser;
                  service?: ServiceForNonTprmUser;
                  thirdPartyIsADraft: boolean;
                  serviceIsADraft: boolean;
                  userCanEditThirdParty: boolean;
                  userCanEditService: boolean;
                  userCanSubmitOrFinalizeThirdParty: boolean;
                  userCanSubmitOrFinalizeService: boolean;
              } = (() => {
            if (rowData.userHasFullAccess) {
                const thirdParty = rowData.organization;
                const service = rowData.assessmentSubjectData?.assessmentSubject;

                const thirdPartyIsADraft = thirdParty.finalization_state !== FinalizationState.FINALIZED;
                const serviceIsADraft = service !== undefined && service.finalization_state !== FinalizationState.FINALIZED;

                const userCanEditThirdParty = true;
                const userCanEditService = true;

                const userCanSubmitOrFinalizeThirdParty = userCanSubmitOrFinalizeDraft(rowData.userId, rowData.userHasFullAccess, thirdParty);
                const userCanSubmitOrFinalizeService = service !== undefined && userCanSubmitOrFinalizeDraft(rowData.userId, rowData.userHasFullAccess, service);

                return { userHasTprmRole: true, thirdParty, service, thirdPartyIsADraft, serviceIsADraft, userCanEditThirdParty, userCanEditService, userCanSubmitOrFinalizeThirdParty, userCanSubmitOrFinalizeService };
            } else {
                const thirdParty = rowData.organization;
                const service = rowData.assessmentSubjectData?.assessmentSubject;

                const thirdPartyIsADraft = thirdParty.finalization_state !== FinalizationState.FINALIZED;
                const serviceIsADraft = service !== undefined && service.finalization_state !== FinalizationState.FINALIZED;

                const userCanEditThirdParty = userHasFullAccessOrCreatedDraft(rowData.userId, rowData.userHasFullAccess, thirdParty);
                const userCanEditService = service !== undefined && userHasFullAccessOrCreatedDraft(rowData.userId, rowData.userHasFullAccess, service);

                const userCanSubmitOrFinalizeThirdParty = userCanSubmitOrFinalizeDraft(rowData.userId, rowData.userHasFullAccess, thirdParty);
                const userCanSubmitOrFinalizeService = service !== undefined && userCanSubmitOrFinalizeDraft(rowData.userId, rowData.userHasFullAccess, service);

                return { userHasTprmRole: false, thirdParty, service, thirdPartyIsADraft, serviceIsADraft, userCanEditThirdParty, userCanEditService, userCanSubmitOrFinalizeThirdParty, userCanSubmitOrFinalizeService };
            }
        })();

        const overflowItems: OverflowItem[] = [];

        const intakeIsInProgress = calculatedInfo.thirdPartyIsADraft || !calculatedInfo.service || calculatedInfo.serviceIsADraft;
        // Users with full access can also complete the intake to add a service to a third party that has none. Users without full access can only complete the intake when something remains for them to submit; once they have submitted their drafts, they can still manage (edit) them, but submitting them again would fail.
        const userCanCompleteIntake = calculatedInfo.userCanSubmitOrFinalizeThirdParty || calculatedInfo.userCanSubmitOrFinalizeService || (calculatedInfo.userHasTprmRole && !calculatedInfo.service);

        if (userCanCompleteIntake) {
            const { thirdParty, service } = calculatedInfo;
            if (service && calculatedInfo.userCanEditService) {
                overflowItems.push({
                    text: 'Complete Intake',
                    onClickAction: () => navigate(buildUrlForCompletingIntake(ProductModule.TPRM, thirdParty.id, service.id)),
                    icon: ICON_EDIT_MODIFY_UPDATE,
                });
            } else {
                overflowItems.push({
                    text: 'Complete Intake',
                    onClickAction: () => navigate(buildUrlForCompletingIntake(ProductModule.TPRM, thirdParty.id)),
                    icon: ICON_EDIT_MODIFY_UPDATE,
                });
            }
        } else {
            const { thirdParty, service } = calculatedInfo;
            if (calculatedInfo.userCanEditThirdParty) {
                overflowItems.push({
                    text: 'Manage Third Party',
                    onClickAction: () => navigate(generatePath(TPRM_UPDATE_THIRD_PARTY, { thirdPartyId: thirdParty.id })),
                    icon: ICON_EDIT_MODIFY_UPDATE,
                });
            }

            if (service && calculatedInfo.userCanEditService) {
                overflowItems.push({
                    text: 'Manage Third-Party Service',
                    onClickAction: () => navigate(generatePath(TPRM_UPDATE_THIRD_PARTY_SERVICE, { thirdPartyId: thirdParty.id, serviceId: service.id })),
                    icon: ICON_EDIT_MODIFY_UPDATE,
                });
            }
        }

        if (calculatedInfo.userHasTprmRole && calculatedInfo.service) {
            const { service } = calculatedInfo;
            overflowItems.push({
                text: 'Set Inherent Risk Questionnaire Due Date',
                onClickAction: () => props.onSetIrqTargetCompletionDate(service),
                icon: ICON_CALENDAR,
            });
        }

        if (calculatedInfo.userHasTprmRole && calculatedInfo.service) {
            const { service } = calculatedInfo;
            overflowItems.push({
                text: 'Set Due Diligence Workflow Due Date',
                onClickAction: () => props.onSetAssessmentDueDate(service),
                icon: ICON_CALENDAR,
            });
        }

        if (intakeIsInProgress) {
            if (calculatedInfo.userCanEditThirdParty) {
                overflowItems.push({
                    text: 'Delete Third Party',
                    onClickAction: () => props.onDeleteThirdParty(calculatedInfo.thirdParty),
                    icon: ICON_DELETE_REMOVE,
                });
            }

            const { service } = calculatedInfo;
            if (service && calculatedInfo.userCanEditService) {
                overflowItems.push({
                    text: 'Delete Third-Party Service',
                    onClickAction: () => props.onDeleteService(service),
                    icon: ICON_DELETE_REMOVE,
                });
            }
        }

        if (calculatedInfo.userHasTprmRole) {
            overflowItems.push({
                text: 'Toggle active/inactive statuses',
                onClickAction: () => props.onSetActiveStates(calculatedInfo.thirdParty),
                icon: faSliders,
            });
        }

        if (overflowItems.length === 0) {
            overflowItems.push({
                text: 'Close Menu (no actions available)',
                onClickAction: () => {},
                icon: ICON_CLOSE,
            });
        }

        const overflowMenuProps: OverflowMenuProps = {
            overflowItems: overflowItems,
            accessibilityTitle: calculatedInfo.service ? `Open menu for ${calculatedInfo.thirdParty.name}: ${calculatedInfo.service.name}` : `Open menu for ${calculatedInfo.thirdParty.name}`,
        };

        return <OverflowMenu {...overflowMenuProps} />;
    };

    const getSecondaryString = (thirdPartyOrService: { active_state: ActiveState; finalization_state: FinalizationState } | undefined): string | undefined => {
        if (thirdPartyOrService === undefined) {
            return undefined;
        }

        let combinedStatesString = '';
        if (thirdPartyOrService.active_state !== ActiveState.ACTIVE) {
            combinedStatesString += thirdPartyOrService.active_state;
        }
        if (thirdPartyOrService.finalization_state !== FinalizationState.FINALIZED) {
            combinedStatesString += combinedStatesString === '' ? thirdPartyOrService.finalization_state : `, ${thirdPartyOrService.finalization_state}`;
        }
        return combinedStatesString === '' ? undefined : combinedStatesString;
    };

    const columns: ColumnConfig<OrganizationsAndAssessmentSubjectsTableRowData<TprmEntities>>[] = [
        {
            type: ColumnType.PRIMARY_AND_SECONDARY_STRING,
            name: 'Third Party',
            getPrimaryString: (data) => data.organization.name,
            getSecondaryString: (data) => getSecondaryString(data.organization),
        },
        {
            type: ColumnType.PRIMARY_AND_SECONDARY_STRING,
            name: 'Service',
            getPrimaryString: (data) => data.assessmentSubjectData?.assessmentSubject.name,
            getSecondaryString: (data) => getSecondaryString(data.assessmentSubjectData?.assessmentSubject),
            getOnClickPrimaryTextAction: (data) => (props.userTypeAndTableRowsData.userHasFullTprmAccess && data.assessmentSubjectData && data.assessmentSubjectData.assessmentSubject.finalization_state === FinalizationState.FINALIZED ? { goToLocation: generatePath(TPRM_SERVICE_DASHBOARD, { third_party_id: data.organization.id, service_id: data.assessmentSubjectData.assessmentSubject.id }) } : undefined),
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Assessment Progress',
            getTableCellContent: (data) => <DueDiligenceWorkflowProgressTableCellContent rowData={data} />,
            getValue: (data) => getNumericalValueForServiceProgress(data),
            sortComparator: (a, b) => a - b,
        },
        {
            type: ColumnType.RISK_RATING,
            name: 'Inherent Risk',
            getRiskRating: (data) => (data.assessmentSubjectData ? (data.assessmentSubjectData.assessmentSubject.inherent_risk_score ?? 0) : undefined),
        },
        {
            type: ColumnType.RISK_RATING,
            name: 'Residual Risk',
            getRiskRating: (data) => (data.assessmentSubjectData ? (data.assessmentSubjectData.assessmentSubject.residual_risk_score ?? 0) : undefined),
        },
        {
            type: ColumnType.DATE,
            name: 'Due Diligence Workflow Due Date',
            getDateString: (data) => data.assessmentSubjectData?.assessmentSubject.assessment_workflow_setup.due_date,
        },
    ];

    const filters: FilterConfig<OrganizationsAndAssessmentSubjectsTableRowData<TprmEntities>>[] = [
        {
            type: FilterType.USERS,
            id: 'creatorFilter',
            label: 'Filter by Creator',
            getUserIdsForRow: (data) => {
                const userIds: string[] = [];
                if (data.organization.created_by) {
                    userIds.push(data.organization.created_by);
                }
                if (data.assessmentSubjectData?.assessmentSubject.created_by) {
                    userIds.push(data.assessmentSubjectData.assessmentSubject.created_by);
                }
                return userIds;
            },
            defaultSelections: props.userTypeAndTableRowsData.userHasFullTprmAccess ? [] : [props.userTypeAndTableRowsData.userId],
        },
        {
            type: FilterType.USERS,
            id: 'managerFilter',
            label: 'Filter by Manager',
            rolesRestriction: TPRM_ROLES,
            getUserIdsForRow: (data) => {
                const userIds: string[] = [data.organization.vendor_manager_user_id];
                if (data.assessmentSubjectData?.assessmentSubject) {
                    userIds.push(data.assessmentSubjectData.assessmentSubject.vendor_service_manager_user_id);
                }
                return userIds;
            },
        },
        {
            type: FilterType.SINGLE_PROPERTY,
            id: 'statusFilter',
            label: 'Filter by Draft Status',
            options: Object.values(FinalizationState).map((state) => ({
                name: state,
                value: state,
            })),
            getFilterableDataForRow: (data) => {
                const statuses: FinalizationState[] = [data.organization.finalization_state];
                if (data.assessmentSubjectData?.assessmentSubject) {
                    statuses.push(data.assessmentSubjectData.assessmentSubject.finalization_state);
                }
                return statuses;
            },
            canSelectMultiple: true,
        },
        {
            type: FilterType.TAGS,
            id: 'tagsFilter',
            label: 'Filter by Tags',
            getTagIdsForRow: (data) => {
                const thirdPartyTagIds = [...data.organization.tags];
                const serviceTagIds = data.assessmentSubjectData ? [...data.assessmentSubjectData.assessmentSubject.tags] : [];
                return [...thirdPartyTagIds, ...serviceTagIds];
            },
        },
    ];

    const tableProps: TableProps<OrganizationsAndAssessmentSubjectsTableRowData<TprmEntities>> = {
        columns,
        filters,
        defaultSortColumn: 'Third Party',
        rowsData: props.userTypeAndTableRowsData.tableRowsData,
        getUniqueIdForRow: (data) => (data.assessmentSubjectData ? `${data.organization.id}${data.assessmentSubjectData.assessmentSubject.id}` : data.organization.id),
        getExpandableElementForRow: (data) => <ThirdPartiesAndServicesTableExpandedInformation folders={data.foldersByOrganizationId?.get(data.organization.id) ?? []} onDisplayContacts={props.onDisplayContacts} onDisplayUsers={props.onDisplayUsers} thirdParty={data.organization} service={data.assessmentSubjectData?.assessmentSubject} userHasFullTprmAccess={props.userTypeAndTableRowsData.userHasFullTprmAccess} />,
        getOverflowMenuForRow,
    };

    return <Table {...tableProps} />;
};
